import React from 'react'
import {
  ExternalUserFieldAccess,
  FormField,
  GeckoFeature,
  getFormFieldByDataKey,
  InputType,
  machinedForgingStageColorMap,
  MachinedForgingStageContainer,
  MillPlanModelType,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  optionalDateStringWithRegex,
  optionalStringSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export enum MachinedForgingComponentStage {
  UNMILLED = 'UNMILLED',
  MILLED = 'MILLED',
}

export type MachinedForgingSimpleAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.MachinedForgingSimple
  stage: string | undefined
  blender_model_id: string | undefined
  manufactured_date: string | undefined
  mill_plan_role: string | undefined
  features: Record<string, never>
  scene_config: Record<string, number>
  component_bounds: Record<string, number>
}

export const MachinedForgingAvailableFeatures: GeckoFeatureModelType[] = []

export const NewMachinedForgingSimpleComponentJSON = {
  name: 'New Machined Forging',
  type: GeckoComponentModelType.MachinedForgingSimple,
  stage: undefined,
  blender_model_id: undefined,
  manufactured_date: undefined,
  mill_plan_role: undefined,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
} as MachinedForgingSimpleAssetModelJSON

export class GeckoMachinedForgingSimple extends GeckoEditableComponent {
  type: GeckoComponentModelType.MachinedForgingSimple
  features: Map<string, GeckoFeature>
  stage: string | undefined
  blender_model_id: string | undefined
  manufactured_date: string | undefined
  mill_plan_role: string | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    stage: string | undefined,
    blender_model_id: string | undefined,
    manufactured_date: string | undefined,
    mill_plan_role: string | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.MachinedForgingSimple
    this.stage = stage
    this.blender_model_id = blender_model_id
    this.manufactured_date = manufactured_date
    this.mill_plan_role = mill_plan_role
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig

    // Features
    this.features = new Map()
  }

  public static CreateFromJSON(
    id: string,
    json: MachinedForgingSimpleAssetModelJSON
  ) {
    const name = json.name
    const stage = json.stage
    const blender_model_id = json.blender_model_id
    const manufactured_date = json.manufactured_date
    const mill_plan_role = json.mill_plan_role

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfigData = json.scene_config as Record<string, number>
    const sceneConfig = {
      config_2d: {
        position: [sceneConfigData.x_2d, sceneConfigData.y_2d],
        rotation: 0,
      },
    } as SceneConfig

    return new GeckoMachinedForgingSimple(
      id,
      json,
      name,
      stage,
      blender_model_id,
      manufactured_date,
      mill_plan_role,
      componentBounds,
      sceneConfig
    )
  }

  public asJSON(): MachinedForgingSimpleAssetModelJSON {
    return generateMachinedForging(
      this.name,
      this.json as MachinedForgingSimpleAssetModelJSON,
      this.stage,
      this.blender_model_id,
      this.manufactured_date,
      this.mill_plan_role
    )
  }
}

const generateMachinedForging = (
  name: string,
  otherMachinedForgingData: MachinedForgingSimpleAssetModelJSON = NewMachinedForgingSimpleComponentJSON,
  stage: string | undefined,
  blender_model_id: string | undefined,
  manufactured_date: string | undefined,
  mill_plan_role: string | undefined
): MachinedForgingSimpleAssetModelJSON => {
  const featureSetToUse: MachinedForgingSimpleAssetModelJSON['features'] = {
    ...otherMachinedForgingData.features,
  }
  return {
    name: name,
    type: GeckoComponentModelType.MachinedForgingSimple,
    stage: stage,
    blender_model_id: blender_model_id,
    manufactured_date: manufactured_date,
    mill_plan_role: mill_plan_role,
    features: featureSetToUse,
    scene_config: otherMachinedForgingData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: 0,
      y_end: 0,
    },
  }
}

export const MachinedForgingSimpleAssetModelFormFields = (
  machinedForgingSimpleJSON: MachinedForgingSimpleAssetModelJSON
): FormField[] => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: machinedForgingSimpleJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'stage',
      label: 'Stage',
      value: machinedForgingSimpleJSON.stage,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: Object.values(MachinedForgingComponentStage).map((stage) => ({
        label: React.createElement(
          MachinedForgingStageContainer,
          {
            bgColor: machinedForgingStageColorMap.get(stage),
            textColor: '#000',
          },
          stage
        ),
        value: stage,
      })),
    },
    {
      dataKey: 'blender_model_id',
      label: 'Model ID',
      value: machinedForgingSimpleJSON.blender_model_id,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'manufactured_date',
      label: 'Manufactured Date',
      value: machinedForgingSimpleJSON.manufactured_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'mill_plan_role',
      label: 'Mill Plan Role',
      value: machinedForgingSimpleJSON.mill_plan_role,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: Object.values(MillPlanModelType).map((type) => ({
        label: type,
        value: type,
      })),
    },
  ]
}

export const MachinedForgingSimpleJSONFromFormFields = (
  component: MachinedForgingSimpleAssetModelJSON,
  formFields: FormField[]
): MachinedForgingSimpleAssetModelJSON => {
  const stage = getFormFieldByDataKey(formFields, 'stage')

  return generateMachinedForging(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    component,
    stage?.value ? (stage.value as string) : undefined,
    getFormFieldByDataKey(formFields, 'blender_model_id')?.value as string,
    getFormFieldByDataKey(formFields, 'manufactured_date')?.value as
      | string
      | undefined,
    getFormFieldByDataKey(formFields, 'mill_plan_role')?.value as
      | string
      | undefined
  )
}
